<script setup lang="ts">
import type { HTMLAttributes } from 'vue'

import { cn } from 'cn'
import { ArrowDownIcon } from 'lucide-vue-next'

import type { MessageScrollerButtonDirection } from './useMessageScroller'

import { useMessageScroller, useMessageScrollerScrollable } from './useMessageScroller'

const props = withDefaults(defineProps<{
  class?: HTMLAttributes['class']
  direction?: MessageScrollerButtonDirection
  behavior?: ScrollBehavior
}>(), {
  direction: 'end',
  behavior: 'smooth',
})

const { scrollToEnd, scrollToStart } = useMessageScroller()
const scrollable = useMessageScrollerScrollable()

const active = computed(() =>
  props.direction === 'start' ? scrollable.value.start : scrollable.value.end)

function onClick(event: MouseEvent) {
  if (!active.value)
    return
  const target = event.currentTarget as HTMLElement | null
  target?.blur()
  if (event.defaultPrevented)
    return
  if (props.direction === 'start')
    scrollToStart({ behavior: props.behavior })
  else
    scrollToEnd({ behavior: props.behavior })
}
</script>

<template>
  <UiButton
    data-slot="message-scroller-button"
    type="button"
    color="neutral"
    variant="outline"
    size="xs"
    aspect-square
    :data-direction="direction"
    :data-active="active ? 'true' : 'false'"
    :inert="!active"
    :tabindex="active ? undefined : -1"
    :class="cn(
      'absolute left-1/2 -translate-x-1/2 border-border bg-background text-foreground transition-[transform,opacity] duration-200 hover:bg-muted hover:text-foreground data-[active=false]:pointer-events-none data-[active=false]:scale-95 data-[active=false]:opacity-0 data-[active=true]:translate-y-0 data-[active=true]:scale-100 data-[active=true]:opacity-100 data-[direction=end]:bottom-4 data-[direction=end]:data-[active=false]:translate-y-full data-[direction=start]:top-4 data-[direction=start]:data-[active=false]:-translate-y-full data-[direction=start]:[&_svg]:rotate-180',
      props.class,
    )"
    @click="onClick"
  >
    <slot>
      <ArrowDownIcon class="size-4" />
      <span class="sr-only">{{ direction === 'end' ? 'Scroll to end' : 'Scroll to start' }}</span>
    </slot>
  </UiButton>
</template>
